import React, { useState, useEffect } from "react";
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { logEvent } from "../../utils/LogEvent";

const departmentOptions = [
  "Engineering",
  "Computer Studies and Multimedia Arts",
  "HSC",
  "MPS",
];
const positionOptions = [
  "Program Director",
  "Director",
  "Assistant Director",
  "Professor",
];
const roleOptions = ["admin", "user"];
const statusOptions = ["active", "inactive", "pending"];

function ViewUsers() {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editData, setEditData] = useState({
    fullName: "",
    email: "",
    employeeCode: "",
    department: "",
    position: "",
    role: "",
    status: "",
    contactNumber: "",
  });
  const [message, setMessage] = useState("");

  // Fetch users from Firestore
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
      const usersData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    });
    return () => unsubscribe();
  }, []);

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditData({
      fullName: user.fullName || "",
      email: user.email || "",
      employeeCode: user.employeeCode || "",
      department: user.department || "",
      position: user.position || "",
      role: user.role || "",
      status: user.status || "",
      contactNumber: user.contactNumber || "",
    });
    setEditOpen(true);
  };

  const handleEditChange = (e) => {
    setEditData({
      ...editData,
      [e.target.name]: e.target.value,
    });
  };

  const handleEditSubmit = async () => {
    try {
      if (!selectedUser) return;
      const userRef = doc(db, "users", selectedUser.id);
      await updateDoc(userRef, editData);

      await logEvent(
        "edit",
        { userId: selectedUser.id, changes: editData },
        "adminUID"
      );

      setMessage("User updated successfully.");
      setEditOpen(false);
    } catch (err) {
      setMessage("Error updating user: " + err.message);
    }
  };

  // DataGrid columns
  const columns = [
    { field: "fullName", headerName: "Full Name", flex: 1, minWidth: 140 },
    { field: "email", headerName: "Email", flex: 1.2, minWidth: 170 },
    { field: "employeeCode", headerName: "Employee Code", minWidth: 110 },
    { field: "department", headerName: "Department", flex: 1.3, minWidth: 160 },
    { field: "position", headerName: "Position", minWidth: 120 },
    {
      field: "role",
      headerName: "Role",
      minWidth: 90,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            background:
              params.value === "admin" ? theme.palette.primary.main : "#23263b",
            color:
              params.value === "admin" ? "#fff" : theme.palette.text.primary,
            fontWeight: 500,
            fontSize: 13,
            px: 1,
          }}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            background:
              params.value === "active"
                ? "#25c2a0"
                : params.value === "pending"
                ? "#ffc858"
                : "#23263b",
            color:
              params.value === "active" ? "#fff" : theme.palette.text.primary,
            fontWeight: 500,
            fontSize: 13,
            px: 1,
          }}
        />
      ),
    },
    { field: "contactNumber", headerName: "Contact Number", minWidth: 120 },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      minWidth: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          sx={{
            background: theme.palette.primary.main,
            color: "#fff",
            borderRadius: 2,
            boxShadow: "none",
            fontWeight: 600,
            px: 2,
            "&:hover": {
              background: "#1769aa",
            },
          }}
          onClick={() => handleEditClick(params.row)}
        >
          EDIT
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: 1300, mx: "auto", mt: 0 }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          mb: 2,
        }}
      >
        View Users
      </Typography>
      {message && (
        <Typography variant="body1" color="primary" sx={{ mb: 2 }}>
          {message}
        </Typography>
      )}

      <Box
        sx={{
          height: 420,
          width: "100%",
          background: "#0B0D0E", // Table body: true black
          borderRadius: 2,
          border: "1px solid rgba(255,255,255,0.07)",
          color: "#f3f6fa",
          "& .MuiDataGrid-root": {
            border: "none",
            background: "#0B0D0E",
            color: "#f3f6fa",
          },
          "& .MuiDataGrid-columnHeaders": {
            background: "#171A1C", // Table header
            color: "#CDD7E1",
            fontWeight: 700,
            fontSize: 15,
            borderBottom: "1px solid rgba(255,255,255,0.07)",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            fontSize: 15,
            color: "#9FA6AD",
          },
          "& .MuiDataGrid-row:hover": {
            background: "#181B20", // Lighter hover, matches sidebar/cards
          },
          "& .MuiDataGrid-footerContainer, & .MuiTablePagination-root": {
            background: "#0B0D0E",
            color: "#f3f6fa",
            borderTop: "1px solid rgba(255,255,255,0.07)",
          },
        }}
      >
        <DataGrid
          rows={users}
          columns={columns}
          disableRowSelectionOnClick
          autoHeight={false}
          hideFooterSelectedRowCount
          sx={{
            fontFamily: theme.typography.fontFamily,
          }}
          pageSizeOptions={[10, 20, 50]}
        />
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        PaperProps={{
          sx: {
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRadius: 2,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>
          Edit User
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Full Name"
            name="fullName"
            value={editData.fullName}
            onChange={handleEditChange}
            fullWidth
            variant="outlined"
            InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
          />
          <TextField
            margin="dense"
            label="Employee Code"
            name="employeeCode"
            value={editData.employeeCode}
            onChange={handleEditChange}
            fullWidth
            variant="outlined"
            InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
          />
          {/* Department dropdown */}
          <TextField
            select
            margin="dense"
            label="Department"
            name="department"
            value={editData.department}
            onChange={handleEditChange}
            fullWidth
            variant="outlined"
            InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
          >
            {departmentOptions.map((dept) => (
              <MenuItem key={dept} value={dept}>
                {dept}
              </MenuItem>
            ))}
          </TextField>
          {/* Position dropdown */}
          <TextField
            select
            margin="dense"
            label="Position"
            name="position"
            value={editData.position}
            onChange={handleEditChange}
            fullWidth
            variant="outlined"
            InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
          >
            {positionOptions.map((pos) => (
              <MenuItem key={pos} value={pos}>
                {pos}
              </MenuItem>
            ))}
          </TextField>
          {/* Role dropdown */}
          <TextField
            select
            margin="dense"
            label="Role"
            name="role"
            value={editData.role}
            onChange={handleEditChange}
            fullWidth
            variant="outlined"
            InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
          >
            {roleOptions.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          {/* Status dropdown */}
          <TextField
            select
            margin="dense"
            label="Status"
            name="status"
            value={editData.status}
            onChange={handleEditChange}
            fullWidth
            variant="outlined"
            InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
          >
            {statusOptions.map((stat) => (
              <MenuItem key={stat} value={stat}>
                {stat}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Contact Number"
            name="contactNumber"
            value={editData.contactNumber}
            onChange={handleEditChange}
            fullWidth
            variant="outlined"
            InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ViewUsers;
